import './InputField.scss';
import { InputProps, Form, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import React, { ReactNode, useState } from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Block, FlexBox, Spacer } from '../../layouts';
import { TextCommon } from '../../typo';
import { FormConnector } from '../FormConnector';
import { AppButton } from '../../buttons';
import { PhoneInput, PhoneInputProps } from 'react-international-phone';
import { PhoneNumberUtil } from 'google-libphonenumber';
import './InputPhoneField.scss';
const phoneUtil = PhoneNumberUtil.getInstance();

interface InputFieldProps extends PhoneInputProps {
    readonly type?: InputProps['type'];
    readonly name: string;
    readonly label?: string;
    readonly placeholder?: string;
    readonly rules?: UseControllerProps['rules'];
    readonly addonBefore?: ReactNode;
    readonly addonAfter?: ReactNode;
    readonly suffix?: ReactNode;
    readonly tooltipContent?: string;
    readonly disabled?: boolean;
    readonly description?: string;
    readonly maxLength?: number;
    readonly handlePhoneChange: (countryCode: string, nationalNumber: string) => void;
    readonly nationalNumber: string;
    readonly countryCode: string;
}

const getInitialPhone = (
    nationalNumber: string,
    countryCode?: string,
) => {
    let finalCountryCode = countryCode || '';

    if (!finalCountryCode || !/^\d+$/.test(finalCountryCode)) {
        try {
            const exampleNum = phoneUtil.getExampleNumber(finalCountryCode.toUpperCase());
            finalCountryCode = `${exampleNum.getCountryCode()}`;
        } catch {
            finalCountryCode = '';
        }
    }

    if (finalCountryCode) {
        return `${finalCountryCode}${nationalNumber}`;
    }

    return '';
};

export const InputPhoneField = (props: React.PropsWithChildren<InputFieldProps>) => {
    const { t } = useTranslation();
    const [isFocused, setIsFocused] = useState(false);
    const [isHover] = useState(false);
    const [fullPhone, setFullPhone] = useState(getInitialPhone(props.nationalNumber, props.countryCode));
    const { label, name, placeholder, type, rules, addonBefore, addonAfter, tooltipContent } = props;
    const onRender = React.useCallback(
        (renderProps: any) => {
            const { value } = renderProps.field;
            const { error } = renderProps.fieldState;

            const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
                setIsFocused(false);
                const trimmedValue = trimText(value);
                try {
                    const parsed = phoneUtil.parseAndKeepRawInput(trimmedValue);
                    phoneUtil.isValidNumber(parsed);
                    props.handlePhoneChange(`${parsed.getCountryCode()}`, parsed.getNationalNumber()?.toString() || '');

                } catch (error) {
                    console.log('Invalid');
                }
                props?.onBlur?.(e);
            };

            const handleChange = (value: string) => {
                setFullPhone(value);
                const trimmedValue = trimText(value);
                try {
                    const parsed = phoneUtil.parseAndKeepRawInput(trimmedValue);
                    phoneUtil.isValidNumber(parsed);
                    props.handlePhoneChange(`${parsed.getCountryCode()}`, parsed.getNationalNumber()?.toString() || '');

                } catch (error) {
                    console.log('Invalid');
                }
            };

            const trimText = (input: string) => {
                return input.trim();
            };

            return (
                <Form.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message && t(error?.message)}
                >
                    <FlexBox className='app-input-field' width={'100%'}>
                        <PhoneInput
                            defaultCountry={props.countryCode}
                            value={fullPhone}
                            onChange={handleChange}
                            placeholder={props.placeholder}
                            onBlur={handleBlur}
                        />
                    </FlexBox>
                </Form.Item>
            );
        },
        [addonAfter, addonBefore, isFocused, isHover, name, placeholder, props, t, type]
    );

    const isRequired = !!rules?.required;
    return (
        <FormConnector>
            {(form) => {
                const { control } = form;
                return (
                    <Block width='100%'>
                        {
                            label && <Spacer bottom={6}>
                                <TextCommon
                                    fontWeight={600}
                                    lineHeight='20px'
                                    color='#344054'
                                >
                                    {t(label)}
                                </TextCommon>
                                {isRequired && (
                                    <span style={{ color: 'red' }}> *</span>
                                )}
                                {tooltipContent && <Tooltip placement='top' title={<span>{t(tooltipContent)}</span>}>
                                    <AppButton type='text' icon={<QuestionCircleOutlined />} />
                                </Tooltip>}
                            </Spacer>
                        }
                        {props.description && (<Spacer bottom={4}><TextCommon>{t(props.description)}</TextCommon></Spacer>)}
                        <Controller
                            name={name}
                            control={control}
                            render={onRender}
                            rules={rules}
                        />
                    </Block>
                );
            }}
        </FormConnector>
    );
};
